import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Chip,
  Grid,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Psychology, TrendingUp, TrendingDown, ExpandMore } from '@mui/icons-material';
import { apiService, MLPrediction } from '../services/api';
import { useTheme } from '@mui/material/styles';
import { LineChart, ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { LikelyRangeCard, RangeReliability } from '../components/LikelyRange';

// "MAX" is all the history Yahoo has for the ticker; the backend serves it (and every length
// while TWS is offline) from the OHLCV store.
const HISTORY_PERIODS: [string, string][] = [
  ['1 D', '1 Day'], ['1 W', '1 Week'], ['1 M', '1 Month'], ['3 M', '3 Months'], ['1 Y', '1 Year'],
  ['3 Y', '3 Years'], ['5 Y', '5 Years'], ['10 Y', '10 Years'], ['15 Y', '15 Years'], ['20 Y', '20 Years'],
  ['MAX', 'Max (all history)'],
];
const HISTORY_LABEL: Record<string, string> = Object.fromEntries(HISTORY_PERIODS);
// Trading days: a month is ~21, a year 252
const HORIZONS: [number, string][] = [
  [5, '1 week'], [10, '2 weeks'], [21, '1 month'], [42, '2 months'], [63, '3 months'],
  [126, '6 months'], [252, '1 year'], [756, '3 years'], [1260, '5 years'], [2520, '10 years'],
  [3780, '15 years'],
];
const MAX_CHART_POINTS = 1500; // decades of daily bars are thinned to this many for the chart

// What a run actually used, as the backend reports it, so a selection that was dropped shows
function InputsUsed({ p }: { p: MLPrediction }) {
  const tickers = p.tickers_used ?? [];
  const names = p.features_used ?? [];
  const kind = p.model_type === 'chronos2' ? 'covariates' : 'features';
  return (
    <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
        Inputs used
      </Typography>
      <Typography variant="caption" fontWeight={600} display="block">
        Tickers: {tickers.length ? tickers.join(', ') : 'none'} · {names.length} {kind}
      </Typography>
      {names.length > 0 && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ wordBreak: 'break-word' }}>
          {names.join(', ')}
        </Typography>
      )}
      {(p.skipped?.length ?? 0) > 0 && (
        <Alert severity="warning" sx={{ mt: 1, py: 0 }}>
          Not used (not in the data): {p.skipped!.join(', ')}
        </Alert>
      )}
    </Box>
  );
}

const MLPredictions: React.FC = () => {
  const theme = useTheme();
  const [symbol, setSymbol] = useState('');
  const [predictionDays, setPredictionDays] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [xgboostPrediction, setXgboostPrediction] = useState<MLPrediction | null>(null);
  const [chronos2Prediction, setChronos2Prediction] = useState<MLPrediction | null>(null);
  const [historicalPeriod, setHistoricalPeriod] = useState('1 M');
  const [historicalData, setHistoricalData] = useState<any[]>([]);
  const [historyInfo, setHistoryInfo] = useState<{ label: string; from: string; to: string; bars: number } | null>(null);
  const [logScale, setLogScale] = useState(false);
  const [selectedCovariates, setSelectedCovariates] = useState<{[key: string]: boolean}>({
    volume: false,
    rsi: false,
    macd: false,
    bb_upper: false,
    bb_lower: false,
    sma_20: false,
    ema_20: false,
  });
  const [selectedXGBoostFeatures, setSelectedXGBoostFeatures] = useState<{[key: string]: boolean}>({
    volume: false,
    rsi: false,
    macd: false,
    bb_upper: false,
    bb_lower: false,
    sma_20: false,
    ema_20: false,
  });
  const [additionalTickers, setAdditionalTickers] = useState<string>('SPY,QQQ');

  // `pick` runs a ticker chosen from the range table without waiting for the symbol state
  const handlePredict = async (pick?: string) => {
    const sym = (pick ?? symbol).toUpperCase();
    if (!sym) return;

    setLoading(true);
    setError(null);
    setXgboostPrediction(null);
    setChronos2Prediction(null);

    const errors: string[] = [];

    // Try XGBoost prediction, auto-train if needed
    const featuresList = Object.keys(selectedXGBoostFeatures).filter(key => selectedXGBoostFeatures[key]);
    const tickersList = additionalTickers
      .split(',')
      .map(t => t.trim().toUpperCase())
      .filter(t => t.length > 0);

    const xgboostPromise = apiService.getMLPrediction(
      sym,
      predictionDays,
      tickersList.length > 0 ? tickersList : undefined,
      featuresList.length > 0 ? featuresList : undefined
    )
      .then(data => setXgboostPrediction(data))
      .catch(async (err: any) => {
        const errorMsg = err.response?.data?.detail || 'Failed to get XGBoost prediction';
        if (errorMsg.includes('Model not found') || errorMsg.includes('not trained')) {
          // Auto-train the model
          try {
            // Training needs prediction_days + 250 bars; one year holds ~257, so even
            // 10 days failed on '1 Y'. 63 → 2 Y and 126 → 3 Y are what Feature 43 tested.
            let trainingDuration = '2 Y';
            if (predictionDays > 1260) trainingDuration = '20 Y';
            else if (predictionDays > 756) trainingDuration = '15 Y';
            else if (predictionDays > 252) trainingDuration = '10 Y';
            else if (predictionDays > 180) trainingDuration = '5 Y';
            else if (predictionDays > 90) trainingDuration = '3 Y';
            else if (predictionDays > 60) trainingDuration = '2 Y';

            await apiService.trainModel(
              sym,
              predictionDays,
              trainingDuration,
              featuresList.length > 0 ? featuresList : undefined,
              tickersList.length > 0 ? tickersList : undefined
            );

            // Retry prediction after training
            const data = await apiService.getMLPrediction(
              sym,
              predictionDays,
              tickersList.length > 0 ? tickersList : undefined,
              featuresList.length > 0 ? featuresList : undefined
            );
            setXgboostPrediction(data);
          } catch (trainErr: any) {
            errors.push(`XGBoost: Failed to auto-train and predict - ${trainErr.response?.data?.detail || trainErr.message}`);
          }
        } else {
          errors.push(`XGBoost: ${errorMsg}`);
        }
      });

    // Chronos-2 prediction with covariates and additional tickers
    const covariatesList = Object.keys(selectedCovariates).filter(key => selectedCovariates[key]);
    const chronos2Promise = apiService.getChronos2Prediction(
      sym,
      predictionDays,
      covariatesList.length > 0 ? covariatesList : undefined,
      tickersList.length > 0 ? tickersList : undefined
    )
      .then(data => setChronos2Prediction(data))
      .catch((err: any) => {
        errors.push(`Chronos-2: ${err.response?.data?.detail || 'Failed to get prediction'}`);
      });

    // Fetch historical data
    const historicalPromise = apiService.getHistoricalData(sym, historicalPeriod, '1 day')
      .then(data => {
        const bars: any[] = data.bars ?? [];
        if (bars.length) {
          const step = Math.ceil(bars.length / MAX_CHART_POINTS);
          setHistoricalData(bars
            .filter((_, i) => i % step === 0 || i === bars.length - 1)
            .map((bar) => ({ date: new Date(bar.date).toLocaleDateString(), close: bar.close })));
          const first = new Date(bars[0].date), last = new Date(bars[bars.length - 1].date);
          setHistoryInfo({
            label: HISTORY_LABEL[historicalPeriod] ?? historicalPeriod,
            from: first.toLocaleDateString(), to: last.toLocaleDateString(), bars: bars.length,
          });
          // Over a decade or more, a linear axis flattens the early years into a line
          setLogScale(last.getTime() - first.getTime() >= 10 * 365.25 * 86400000);
        }
      })
      .catch((err: any) => {
        console.error('Failed to fetch historical data:', err);
        setHistoricalData([]);
        setHistoryInfo(null);
      });

    await Promise.all([xgboostPromise, chronos2Promise, historicalPromise]);

    if (errors.length > 0 && !xgboostPrediction && !chronos2Prediction) {
      setError(errors.join('\n'));
    }

    setLoading(false);
  };

  const pct = (price: number, current: number) => {
    const v = (price / current - 1) * 100;
    return `${v > 0 ? '+' : ''}${v.toFixed(1)}%`;
  };

  const getPredictionColor = (changePct: number) => {
    if (changePct > 0) return theme.palette.success.main;
    if (changePct < 0) return theme.palette.error.main;
    return theme.palette.text.secondary;
  };

  // Both models report confidence as 0–100
  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 70) return theme.palette.success.main;
    if (confidence >= 50) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Psychology />
        <Typography variant="h4" fontWeight={700}>
          ML Price Predictions
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        AI-powered price forecasting with XGBoost and Chronos-2 models
      </Typography>

      {/* Input Form */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Stock Symbol"
                value={symbol}
                onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                placeholder="AAPL"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Prediction Period</InputLabel>
                <Select
                  value={predictionDays}
                  label="Prediction Period"
                  onChange={(e) => setPredictionDays(Number(e.target.value))}
                >
                  {HORIZONS.map(([days, label]) => (
                    <MenuItem key={days} value={days}>{label} ({days.toLocaleString()} trading days)</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Historical Data</InputLabel>
                <Select
                  value={historicalPeriod}
                  label="Historical Data"
                  onChange={(e) => setHistoricalPeriod(e.target.value)}
                >
                  {HISTORY_PERIODS.map(([value, label]) => (
                    <MenuItem key={value} value={value}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                onClick={() => handlePredict()}
                disabled={loading || !symbol}
                fullWidth
                sx={{ height: '56px' }}
              >
                {loading ? 'Predicting...' : 'Get Predictions'}
              </Button>
            </Grid>
          </Grid>

          {/* Advanced Options */}
          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle2">Advanced Options - Feature Selection & Additional Tickers</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {/* Additional Tickers Section */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Additional Ticker Symbols
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                  Add other ticker symbols (like SPY, QQQ, sector ETFs) to provide market context to both models.
                  Separate multiple tickers with commas.
                </Typography>
                <TextField
                  fullWidth
                  label="Additional Tickers (comma-separated)"
                  value={additionalTickers}
                  onChange={(e) => setAdditionalTickers(e.target.value)}
                  placeholder="SPY, QQQ, DIA, IWM"
                  helperText="Example: SPY,QQQ (market indices) or XLF,XLK (sector ETFs)"
                />
              </Box>

              <Grid container spacing={3}>
                {/* XGBoost Features */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Typography variant="body2" color="primary" fontWeight={600} gutterBottom>
                      XGBoost Features
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                      Select specific features for XGBoost training. If none selected, all 46+ auto-calculated features will be used.
                    </Typography>
                    <FormGroup>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selectedXGBoostFeatures.volume}
                                onChange={(e) => setSelectedXGBoostFeatures({...selectedXGBoostFeatures, volume: e.target.checked})}
                              />
                            }
                            label="Volume"
                            sx={{ mb: 1 }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selectedXGBoostFeatures.rsi}
                                onChange={(e) => setSelectedXGBoostFeatures({...selectedXGBoostFeatures, rsi: e.target.checked})}
                              />
                            }
                            label="RSI"
                            sx={{ mb: 1 }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selectedXGBoostFeatures.macd}
                                onChange={(e) => setSelectedXGBoostFeatures({...selectedXGBoostFeatures, macd: e.target.checked})}
                              />
                            }
                            label="MACD"
                            sx={{ mb: 1 }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selectedXGBoostFeatures.bb_upper}
                                onChange={(e) => setSelectedXGBoostFeatures({...selectedXGBoostFeatures, bb_upper: e.target.checked})}
                              />
                            }
                            label="BB Upper"
                            sx={{ mb: 1 }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selectedXGBoostFeatures.bb_lower}
                                onChange={(e) => setSelectedXGBoostFeatures({...selectedXGBoostFeatures, bb_lower: e.target.checked})}
                              />
                            }
                            label="BB Lower"
                            sx={{ mb: 1 }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selectedXGBoostFeatures.sma_20}
                                onChange={(e) => setSelectedXGBoostFeatures({...selectedXGBoostFeatures, sma_20: e.target.checked})}
                              />
                            }
                            label="SMA 20"
                            sx={{ mb: 1 }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selectedXGBoostFeatures.ema_20}
                                onChange={(e) => setSelectedXGBoostFeatures({...selectedXGBoostFeatures, ema_20: e.target.checked})}
                              />
                            }
                            label="EMA 20"
                            sx={{ mb: 1 }}
                          />
                        </Grid>
                      </Grid>
                    </FormGroup>
                  </Paper>
                </Grid>

                {/* Chronos-2 Covariates */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Typography variant="body2" color="secondary" fontWeight={600} gutterBottom>
                      Chronos-2 Covariates
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                      Select additional covariates for Chronos-2 predictions to provide more market context.
                    </Typography>
                    <FormGroup>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selectedCovariates.volume}
                                onChange={(e) => setSelectedCovariates({...selectedCovariates, volume: e.target.checked})}
                              />
                            }
                            label="Volume"
                            sx={{ mb: 1 }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selectedCovariates.rsi}
                                onChange={(e) => setSelectedCovariates({...selectedCovariates, rsi: e.target.checked})}
                              />
                            }
                            label="RSI"
                            sx={{ mb: 1 }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selectedCovariates.macd}
                                onChange={(e) => setSelectedCovariates({...selectedCovariates, macd: e.target.checked})}
                              />
                            }
                            label="MACD"
                            sx={{ mb: 1 }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selectedCovariates.bb_upper}
                                onChange={(e) => setSelectedCovariates({...selectedCovariates, bb_upper: e.target.checked})}
                              />
                            }
                            label="BB Upper"
                            sx={{ mb: 1 }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selectedCovariates.bb_lower}
                                onChange={(e) => setSelectedCovariates({...selectedCovariates, bb_lower: e.target.checked})}
                              />
                            }
                            label="BB Lower"
                            sx={{ mb: 1 }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selectedCovariates.sma_20}
                                onChange={(e) => setSelectedCovariates({...selectedCovariates, sma_20: e.target.checked})}
                              />
                            }
                            label="SMA 20"
                            sx={{ mb: 1 }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selectedCovariates.ema_20}
                                onChange={(e) => setSelectedCovariates({...selectedCovariates, ema_20: e.target.checked})}
                              />
                            }
                            label="EMA 20"
                            sx={{ mb: 1 }}
                          />
                        </Grid>
                      </Grid>
                    </FormGroup>
                  </Paper>
                </Grid>
              </Grid>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="caption">
                  <strong>Additional Tickers:</strong> Market indices (SPY, QQQ) or sector ETFs provide macro context to both models.
                  {' '}<strong>XGBoost Features:</strong> Select specific features for training (empty = all 46+ features).
                  {' '}<strong>Chronos-2 Covariates:</strong> Select additional technical indicators for context (optional).
                </Typography>
              </Alert>
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>

      <RangeReliability onPick={(s) => { setSymbol(s); if (!loading) handlePredict(s); }} />

      {/* Loading / Error */}
      {loading && (
        <Box sx={{ mb: 3 }}>
          <LinearProgress />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
            Generating predictions (may auto-train models if needed)...
          </Typography>
        </Box>
      )}
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Prediction Results */}
      {(xgboostPrediction || chronos2Prediction) && !loading && (
        <>
          {/* Model Comparison Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                {(xgboostPrediction || chronos2Prediction)?.symbol} - {(xgboostPrediction || chronos2Prediction)?.prediction_days} Trading-Day Forecast Comparison
              </Typography>

              {/* Current Price */}
              <Paper sx={{ p: 2, bgcolor: 'background.default', mb: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  Current Price
                </Typography>
                <Typography variant="h4" fontWeight={700}>
                  ${(xgboostPrediction || chronos2Prediction)?.current_price.toFixed(2)}
                </Typography>
              </Paper>

              {chronos2Prediction?.likely_range && (
                <LikelyRangeCard r={chronos2Prediction.likely_range} current={chronos2Prediction.current_price} />
              )}

              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>How these models tested</strong> (496 S&amp;P 500 stocks, every quarter
                2020–2026): neither called the 3- or 6-month direction better than assuming every
                stock rises (XGBoost 53% / 51%, Chronos-2 53% / 57%, "always up" 59% / 63%), and
                neither picked winners from losers. XGBoost landed further from the real price than
                "no change" (median miss 12% / 17% vs 10% / 13%). Use the likely range above as a guide to
                how far the price may move; treat both middle numbers as no better than "no change".
              </Alert>

              {/* Side-by-Side Predictions */}
              <Grid container spacing={2}>
                {/* XGBoost Prediction */}
                <Grid item xs={12} md={6}>
                  {xgboostPrediction ? (
                    <Paper sx={{ p: 2, border: 2, borderColor: theme.palette.primary.main }}>
                      <Typography variant="h6" gutterBottom color="primary">
                        XGBoost Model
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Predicted Price
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {xgboostPrediction.predicted_change_pct > 0 ? (
                            <TrendingUp sx={{ color: getPredictionColor(xgboostPrediction.predicted_change_pct) }} />
                          ) : (
                            <TrendingDown sx={{ color: getPredictionColor(xgboostPrediction.predicted_change_pct) }} />
                          )}
                          <Typography
                            variant="h5"
                            fontWeight={700}
                            sx={{ color: getPredictionColor(xgboostPrediction.predicted_change_pct) }}
                          >
                            ${xgboostPrediction.predicted_price.toFixed(2)}
                          </Typography>
                          <Chip
                            label={`${xgboostPrediction.predicted_change_pct > 0 ? '+' : ''}${xgboostPrediction.predicted_change_pct.toFixed(2)}%`}
                            size="small"
                            sx={{
                              bgcolor: getPredictionColor(xgboostPrediction.predicted_change_pct) + '20',
                              color: getPredictionColor(xgboostPrediction.predicted_change_pct)
                            }}
                          />
                        </Box>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Range: ${xgboostPrediction.prediction_lower.toFixed(2)} - ${xgboostPrediction.prediction_upper.toFixed(2)}
                          {' '}(spread of its recent fits, not a probability range)
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Confidence
                        </Typography>
                        <Typography
                          variant="h6"
                          fontWeight={600}
                          sx={{ color: getConfidenceColor(xgboostPrediction.confidence) }}
                        >
                          {xgboostPrediction.confidence.toFixed(1)}%
                        </Typography>
                      </Box>
                      {xgboostPrediction.training_start && (
                        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Training Data Range
                          </Typography>
                          <Typography variant="caption" fontWeight={600} display="block">
                            {xgboostPrediction.training_start} to {xgboostPrediction.training_end}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {xgboostPrediction.training_samples} training samples · trained{' '}
                            {xgboostPrediction.trained_at && new Date(xgboostPrediction.trained_at).toLocaleString()}
                            {xgboostPrediction.input_date && ` · predicted from the ${xgboostPrediction.input_date} close`}
                          </Typography>
                        </Box>
                      )}
                      <InputsUsed p={xgboostPrediction} />
                    </Paper>
                  ) : (
                    <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                      <Typography variant="h6" gutterBottom color="text.secondary">
                        XGBoost Model
                      </Typography>
                      <Alert severity="warning">
                        Model not trained. Train the model first to get XGBoost predictions.
                      </Alert>
                    </Paper>
                  )}
                </Grid>

                {/* Chronos-2 Prediction */}
                <Grid item xs={12} md={6}>
                  {chronos2Prediction ? (
                    <Paper sx={{ p: 2, border: 2, borderColor: theme.palette.secondary.main }}>
                      <Typography variant="h6" gutterBottom color="secondary">
                        Chronos-2 Model
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Likely range (80%: 10th–90th percentile)
                        </Typography>
                        <Typography variant="h5" fontWeight={700}>
                          ${chronos2Prediction.prediction_lower.toFixed(2)} – ${chronos2Prediction.prediction_upper.toFixed(2)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {pct(chronos2Prediction.prediction_lower, chronos2Prediction.current_price)} to{' '}
                          {pct(chronos2Prediction.prediction_upper, chronos2Prediction.current_price)} · before
                          correction: held the real price only 67–75% of the time in testing (stocks and ETFs, 1 week
                          to 1 year), missing mostly above the top; the likely range above is corrected
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Middle forecast
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {chronos2Prediction.predicted_change_pct > 0 ? (
                            <TrendingUp sx={{ color: getPredictionColor(chronos2Prediction.predicted_change_pct) }} />
                          ) : (
                            <TrendingDown sx={{ color: getPredictionColor(chronos2Prediction.predicted_change_pct) }} />
                          )}
                          <Typography
                            variant="h6"
                            fontWeight={600}
                            sx={{ color: getPredictionColor(chronos2Prediction.predicted_change_pct) }}
                          >
                            ${chronos2Prediction.predicted_price.toFixed(2)}
                          </Typography>
                          <Chip
                            label={`${chronos2Prediction.predicted_change_pct > 0 ? '+' : ''}${chronos2Prediction.predicted_change_pct.toFixed(2)}%`}
                            size="small"
                            sx={{
                              bgcolor: getPredictionColor(chronos2Prediction.predicted_change_pct) + '20',
                              color: getPredictionColor(chronos2Prediction.predicted_change_pct)
                            }}
                          />
                        </Box>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Confidence
                        </Typography>
                        <Typography
                          variant="h6"
                          fontWeight={600}
                          sx={{ color: getConfidenceColor(chronos2Prediction.confidence) }}
                        >
                          {chronos2Prediction.confidence.toFixed(1)}%
                        </Typography>
                      </Box>
                      {chronos2Prediction.data_start_date && chronos2Prediction.data_end_date && (
                        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Historical Data Range
                          </Typography>
                          <Typography variant="caption" fontWeight={600} display="block">
                            {chronos2Prediction.data_start_date} to {chronos2Prediction.data_end_date}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {chronos2Prediction.data_points} data points
                          </Typography>
                        </Box>
                      )}
                      <InputsUsed p={chronos2Prediction} />
                    </Paper>
                  ) : (
                    <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                      <Typography variant="h6" gutterBottom color="text.secondary">
                        Chronos-2 Model
                      </Typography>
                      <Alert severity="error">
                        Failed to get Chronos-2 prediction.
                      </Alert>
                    </Paper>
                  )}
                </Grid>
              </Grid>

              {/* Historical Price Chart */}
              {historicalData.length > 0 && (
                <Box sx={{ mt: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <Typography variant="subtitle2">
                      Historical Price — {historyInfo?.label}
                      {historyInfo && (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          {historyInfo.from} → {historyInfo.to} · {historyInfo.bars.toLocaleString()} daily bars
                        </Typography>
                      )}
                    </Typography>
                    <FormControlLabel
                      control={<Checkbox size="small" checked={logScale} onChange={(e) => setLogScale(e.target.checked)} />}
                      label={<Typography variant="caption">Log scale</Typography>}
                    />
                  </Box>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={historicalData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" minTickGap={40} />
                      <YAxis
                        scale={logScale ? 'log' : 'auto'}
                        domain={logScale ? ['dataMin', 'dataMax'] : ['auto', 'auto']}
                        allowDataOverflow={logScale}
                        tickFormatter={(v: number) => `$${v < 10 ? v.toFixed(2) : v.toFixed(0)}`}
                      />
                      <Tooltip formatter={(value: any) => `$${value.toFixed(2)}`} />
                      <Line
                        type="monotone"
                        dataKey="close"
                        stroke={theme.palette.info.main}
                        strokeWidth={2}
                        name="Close Price"
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              )}

              {/* Comparison Graph */}
              {(xgboostPrediction || chronos2Prediction) && (
                <Box sx={{ mt: 4 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Price Prediction Comparison
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <ComposedChart
                      data={(() => {
                        const currentPrice = (xgboostPrediction || chronos2Prediction)?.current_price || 0;
                        const predictionDays = (xgboostPrediction || chronos2Prediction)?.prediction_days || 0;
                        const band = new Map((chronos2Prediction?.likely_range?.series ?? []).map(b => [b.day, [b.lower, b.upper]]));

                        // If Chronos-2 has prediction_series, use it for full path
                        if (chronos2Prediction?.prediction_series && chronos2Prediction.prediction_series.length > 0) {
                          return [
                            {
                              day: 0,
                              xgboost: currentPrice,
                              chronos2: currentPrice,
                              band: band.size ? [currentPrice, currentPrice] : undefined,
                            },
                            ...chronos2Prediction.prediction_series.map((point) => ({
                              day: point.day,
                              xgboost: point.day === predictionDays ? xgboostPrediction?.predicted_price || null : null,
                              chronos2: point.price,
                              band: band.get(point.day),
                            }))
                          ];
                        }

                        // Fallback to 2-point comparison if no series available
                        return [
                          {
                            day: 0,
                            xgboost: currentPrice,
                            chronos2: currentPrice,
                          },
                          {
                            day: predictionDays,
                            xgboost: xgboostPrediction?.predicted_price || null,
                            chronos2: chronos2Prediction?.predicted_price || null,
                          }
                        ];
                      })()}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="day"
                        label={{ value: 'Trading days', position: 'insideBottom', offset: -5 }}
                      />
                      <YAxis
                        domain={['auto', 'auto']}
                        label={{ value: 'Price ($)', angle: -90, position: 'insideLeft' }}
                      />
                      <Tooltip
                        formatter={(value: any) => Array.isArray(value)
                          ? `$${value[0].toFixed(2)} – $${value[1].toFixed(2)}`
                          : value ? `$${value.toFixed(2)}` : 'N/A'}
                        labelFormatter={(day: any) => `Day ${day}`}
                      />
                      <Legend />
                      {chronos2Prediction?.likely_range?.series?.length ? (
                        <Area
                          type="monotone"
                          dataKey="band"
                          name="Likely range (80%)"
                          stroke="none"
                          fill={theme.palette.success.main}
                          fillOpacity={0.15}
                          isAnimationActive={false}
                        />
                      ) : null}
                      {xgboostPrediction && (
                        <Line
                          type="monotone"
                          dataKey="xgboost"
                          stroke={theme.palette.primary.main}
                          strokeWidth={3}
                          name="XGBoost"
                          connectNulls
                          dot={{ fill: theme.palette.primary.main, r: 4 }}
                        />
                      )}
                      {chronos2Prediction && (
                        <Line
                          type="monotone"
                          dataKey="chronos2"
                          stroke={theme.palette.secondary.main}
                          strokeWidth={2}
                          name="Chronos-2"
                          connectNulls
                          dot={false}
                        />
                      )}
                    </ComposedChart>
                  </ResponsiveContainer>
                  {chronos2Prediction?.prediction_series && chronos2Prediction.prediction_series.length > 0 && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="body2" gutterBottom>
                        <strong>Why different prediction paths?</strong>
                      </Typography>
                      <Typography variant="caption" component="div">
                        <strong>Chronos-2</strong> is an autoregressive transformer that predicts the full {chronos2Prediction.prediction_series.length}-day sequence,
                        forecasting each day iteratively based on previous predictions.
                      </Typography>
                      <Typography variant="caption" component="div" sx={{ mt: 1 }}>
                        <strong>XGBoost</strong> is a regression model trained to predict a single target value (price at day {chronos2Prediction.prediction_days}).
                        It cannot generate intermediate daily predictions, only the final endpoint.
                      </Typography>
                    </Alert>
                  )}
                </Box>
              )}

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                Predictions generated: {new Date((xgboostPrediction || chronos2Prediction)?.timestamp || '').toLocaleString()}
              </Typography>
            </CardContent>
          </Card>

          {/* Model Info */}
          <Card>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                About These Models
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="primary" fontWeight={600} gutterBottom>
                    XGBoost Model
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Traditional machine learning model that predicts a single future price point using 46+ technical
                    indicators as features. It is trained on this one stock's own 2–3 years, fits that history almost
                    perfectly and then extrapolates it: in testing its forecasts ran far too large, with errors 21–34%
                    bigger than forecasting "no change".
                  </Typography>
                  <Box>
                    <Chip label="Supervised Learning" size="small" sx={{ mr: 1, mb: 1 }} />
                    <Chip label="46+ Features" size="small" sx={{ mr: 1, mb: 1 }} />
                    <Chip label="Single Endpoint Prediction" size="small" sx={{ mb: 1 }} />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="secondary" fontWeight={600} gutterBottom>
                    Chronos-2 Model
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    State-of-the-art zero-shot time series forecasting using a 120M parameter transformer.
                    Requires NO TRAINING and works immediately with any stock. Predicts the full day-by-day sequence
                    with a range from the stock's price history. On stocks its middle forecast stays close to today's
                    price. Its range is useful once corrected, but on stocks a plain past-volatility range did as well
                    or better; it helped most on bond ETFs.
                  </Typography>
                  <Box>
                    <Chip label="Zero-Shot" size="small" color="secondary" sx={{ mr: 1, mb: 1 }} />
                    <Chip label="120M Parameters" size="small" sx={{ mr: 1, mb: 1 }} />
                    <Chip label="Full Sequence Prediction" size="small" sx={{ mb: 1 }} />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default MLPredictions;
